import { useVenues } from "@/hooks/use-venues";
import { Navigation } from "@/components/Navigation";
import { VenueDetailDrawer, PriceMeter } from "@/components/VenueDetailDrawer";
import { useState } from "react";
import type { Venue } from "@shared/schema";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, Users, Flame, GlassWater, Utensils, Coffee, Ban } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const LONDON_BOUNDS = { latMin: 51.28, latMax: 51.70, lngMin: -0.51, lngMax: 0.33 };

export default function ListView() {
  const { data: venues, isLoading } = useVenues(LONDON_BOUNDS);
  const [search, setSearch] = useState("");
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);

  const filteredVenues = venues?.filter(v => 
    v.name.toLowerCase().includes(search.toLowerCase()) || 
    v.type.toLowerCase().includes(search.toLowerCase())
  );

  const getCapacityColor = (level: number) => {
    if (level >= 70) return "text-[hsl(var(--status-high))]";
    if (level >= 31) return "text-[hsl(var(--status-med))]";
    return "text-[hsl(var(--status-low))]";
  };

  const getCapacityBg = (level: number) => {
    if (level >= 70) return "bg-[hsl(var(--status-high))]/20 border-[hsl(var(--status-high))]/20";
    if (level >= 31) return "bg-[hsl(var(--status-med))]/20 border-[hsl(var(--status-med))]/20";
    return "bg-[hsl(var(--status-low))]/20 border-[hsl(var(--status-low))]/20";
  };

  const getTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'bar': return <GlassWater className="w-4 h-4" />;
      case 'restaurant': return <Utensils className="w-4 h-4" />;
      case 'cafe': return <Coffee className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className={`transition-all duration-300 ease-in-out ${selectedVenue ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'}`}>
        <div className="px-6 pb-6 pt-[max(3rem,calc(env(safe-area-inset-top)+1rem))]">
          <h1 className="text-4xl font-display font-bold text-white mb-2">Nearby</h1>
          <p className="text-zinc-400 mb-4">Find out what's happening around you.</p>


          <div className="relative mb-8">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input 
              placeholder="Search venues..." 
              className="pl-10 bg-zinc-900 border-zinc-800 text-white rounded-xl h-12"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="space-y-4">
            {isLoading ? (
              // Skeletons
              Array(5).fill(0).map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-zinc-900/50 border border-zinc-800/50">
                  <Skeleton className="w-12 h-12 rounded-full bg-zinc-800" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-1/3 bg-zinc-800" />
                    <Skeleton className="h-3 w-1/2 bg-zinc-800" />
                  </div>
                </div>
              ))
            ) : (
              filteredVenues?.map(venue => {
                const closed = (venue as any).isCurrentlyOpen === false;
                const noData = venue.capacityLevel === 0 && !closed;

                return (
                <div 
                  key={venue.id}
                  onClick={() => setSelectedVenue(venue)}
                  className="group flex items-center gap-4 p-4 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 transition-all cursor-pointer active:scale-[0.98]"
                >
                  {/* Capacity Circle */}
                  {closed ? (
                    <div className="w-12 h-12 rounded-full flex items-center justify-center border-2 shrink-0 bg-zinc-800/30 border-zinc-700/30">
                      <Ban className="w-4 h-4 text-zinc-500" />
                    </div>
                  ) : noData ? (
                    <div className="w-12 h-12 rounded-full flex items-center justify-center border-2 shrink-0 bg-zinc-800/30 border-zinc-700/30">
                      <span className="text-sm font-bold text-zinc-500">—</span>
                    </div>
                  ) : (
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 shrink-0 ${getCapacityBg(venue.capacityLevel)}`}>
                    <span className={`text-sm font-bold ${getCapacityColor(venue.capacityLevel)}`}>
                      {venue.capacityLevel}%
                    </span>
                  </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-display font-bold text-white text-lg truncate pr-2">{venue.name}</h3>
                      <Badge variant="outline" className="border-white/10 text-xs text-zinc-500 capitalize shrink-0">
                        {venue.type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-500 text-sm">
                      <div className="flex items-center truncate">
                        <MapPin className="w-3 h-3 mr-1 inline shrink-0" />
                        <span className="truncate">{venue.description || "No description available"}</span>
                      </div>
                      {venue.googlePriceLevel && <PriceMeter priceLevel={venue.googlePriceLevel} />}
                    </div>
                  </div>

                  <div className="text-zinc-600">
                    {getTypeIcon(venue.type)}
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className={`fixed bottom-0 left-0 right-0 z-[1000] transition-transform duration-300 ease-in-out ${selectedVenue ? 'translate-y-full' : 'translate-y-0'}`}>
        <Navigation />
      </div>
      
      <VenueDetailDrawer 
        venue={selectedVenue} 
        isOpen={!!selectedVenue} 
        onClose={() => setSelectedVenue(null)} 
      />

    </div>
  );
}
